import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DeepPartial } from '@zaoblako/web/core/types/deep-partial';
import { flatten } from 'flat';
import { Observable } from 'rxjs';

@Injectable()
export class BaseService {
  protected http = inject(HttpClient);
  protected baseUrl: string;

  public find<T>(options?: any): Observable<T[]> {
    return this.http.get<T[]>(this.baseUrl, {
      params: options ? this.objectToHttpParams(options) : {}
    });
  }

  public findById<T>(id: number | string, options?: any): Observable<T> {
    return this.http.get<T>(this.baseUrl + '/' + id, {
      params: options ? this.objectToHttpParams(options) : {}
    });
  }

  public create<T>(model: any): Observable<T> {
    return this.http.post<T>(this.baseUrl, model);
  }

  public update<T, K = void>(model: Partial<T>): Observable<K> {
    return this.http.put<K>(this.baseUrl + '/' + (model as any).id, model);
  }

  public patch<T extends { id?: number }, K = void>(model: DeepPartial<T>): Observable<K> {
    return this.http.patch<K>(this.baseUrl + '/' + model.id, model);
  }

  public deleteById<T>(id: number, options?: any): Observable<T> {
    return this.http.delete<T>(this.baseUrl + '/' + id, {
      params: options ? this.objectToHttpParams(options) : {}
    });
  }

  public objectToHttpParams(params: Record<string, any>): HttpParams {
    // Grab all query parameters into dictionary and then flatten it
    const dict: Record<string, any> = flatten(params || {});

    // Omit all `null` and `undefined` values, all empty objects and arrays from query parameters
    Object.keys(dict).forEach((key) => {
      if (
        dict[key] === null ||
        dict[key] === undefined ||
        (Array.isArray(dict[key]) && dict[key].length < 1) ||
        (typeof dict[key] === 'object' && !Object.keys(dict[key])?.length)
      ) {
        delete dict[key];
      }
    });

    // Make new Angular query parameters and fill them with flattened dictionary data
    let httpParams = new HttpParams();
    Object.keys(dict).forEach((key) => {
      const value = dict[key];
      if (Array.isArray(value)) {
        value.forEach((item) => {
          httpParams = httpParams.append(key, item);
        });
      } else {
        httpParams = httpParams.set(key, value);
      }
    });

    return httpParams;
  }
}
